import type { ReactNode } from "react";
import { AdConsentLink, SocialLinks } from "@suite/ui";

const HOME = "https://ottercraft.dev";

/**
 * The static, crawlable half of the page: what OtterStudio is, how an edit
 * goes, and the questions that come up. No runtime state, so it is
 * prerendered into index.html at build time (see prerender.tsx) and rendered
 * again by the app on mount.
 *
 * Short on purpose. It sits under a tool; the long-form guides live on
 * ottercraft.dev.
 */
export function Explainer() {
  return (
    <section id="about" aria-labelledby="about-title" className="mt-16 border-t border-rule pt-12">
      <p className="eyebrow">About OtterStudio</p>
      <h2
        id="about-title"
        className="mt-2 max-w-[620px] font-serif text-[26px] font-semibold leading-[1.1] tracking-[-0.01em] text-pine sm:text-[30px]"
      >
        A video editor that runs in the tab.
      </h2>
      <p className="mt-3 max-w-[620px] text-[14.5px] leading-[1.6] text-soft">
        Drop in a video, or a few, and some audio. Cut the boring bits out, put a second video in
        the corner, lay a title or a logo over the top, turn the music down under the talking,
        take the hiss out of a recording, record a voiceover or a webcam clip, choose the shape
        the frame should be, and save an MP4. All of it happens in your browser, using the video
        hardware your computer already has. Nothing is uploaded, because there is nowhere to
        upload it to.
      </p>

      <ol className="mt-8 grid gap-6 sm:grid-cols-3">
        <Step n="01" title="Add your files">
          Drag them onto the page or press <em>Add file</em>: video, audio, or a picture, PNG
          transparency included. Each one gets a track of its own, starting at zero. A video with
          sound gets two: the picture on a video track and the sound on an audio track, so you
          can turn one down or swap it without touching the other. <em>Add text</em> puts a title
          at the playhead, with a box, an outline and a way in and out if you want them.
        </Step>
        <Step n="02" title="Cut and arrange">
          Drag clips along the timeline. Select one and pull its ends to trim it, or press{" "}
          <em>Split at playhead</em> to cut it in two. Drag a video around on the picture to place
          it, or use the corner buttons in the clip card. Faders on every audio track.
        </Step>
        <Step n="03" title="Shape it and export">
          The frame takes its size from the first video, or from you: Landscape, Vertical for
          Reels, TikTok and Shorts, Square, or any width and height. Then pick a resolution,
          frame rate and quality and press <em>Start export</em>. Frames are drawn from the
          preview and encoded as you watch, so the tab has to stay in front. A timeline with
          only sound on it saves an M4A instead.
        </Step>
      </ol>

      <p className="mt-8 max-w-[680px] text-[13.5px] leading-[1.6] text-soft">
        Underneath, the browser&rsquo;s own video decoder plays your clips, a canvas composes each
        frame, and WebCodecs, the browser&rsquo;s built-in encoder, turns those frames back into
        H.264. The MP4 is assembled in memory and handed to you as a download. Your edit is kept in
        this browser as a description: which files, where each cut is, where each clip sits. The
        files themselves are never stored, which is why a reopened project asks for them again.
      </p>

      <h3 id="faq" className="mt-12 scroll-mt-6 font-serif text-[20px] font-semibold leading-tight text-pine">
        Questions and troubleshooting
      </h3>
      <div className="mt-3 divide-y divide-rule border-y border-rule">
        <Faq q="Why does it ask for my files when I reopen a project?">
          Because we never kept them. The project is a small record of what you did, saved in your
          browser; the video itself stayed in your folder. When you open the project again, point at
          the same files and it checks they are the ones you used. In Chrome and Edge, if you added
          a file through the file picker, an <em>Open the files</em> button can ask the browser for
          them with one permission prompt. Skip a file and its clip stays on the timeline as a grey
          placeholder until you supply it.
        </Faq>
        <Faq q="Which browsers can export?">
          Editing and playback work in any current browser. Exporting needs the browser to have a
          video encoder, which Chrome, Edge and recent Safari do, and Firefox does not yet. If your
          browser cannot export, the page says so before you start.
        </Faq>
        <Faq q="How large a video can it take?">
          Whatever your device can play. 4K footage from a phone or drone is fine; the preview is
          drawn at the size of your screen, so it stays smooth, and the export renders at the size
          you choose. A long 4K export takes a while, roughly as long as the video itself on a
          laptop, and you can watch it go.
        </Faq>
        <Faq q="Is the export the same quality as the original?">
          It is re-encoded, so not bit for bit. At <em>Standard</em> quality most footage looks the
          same to the eye and the file is often smaller than what came out of the camera. Pick{" "}
          <em>High</em> for something that will be re-encoded again by whoever you send it to. The
          size the dialog shows is a ceiling: the encoder spends less on still scenes.
        </Faq>
        <Faq q="What does Remove noise do?">
          It listens to the quietest moments of an audio clip, takes them as a picture of the
          background noise (hiss, hum, room tone, a fan), and turns those frequencies down
          everywhere else. Speech stays where it is. The cleaned copy lands on a new track under the
          original, which is muted rather than deleted, so you can compare them and undo. It needs a
          quiet moment somewhere in the clip to learn from.
        </Faq>
        <Faq q="Can I record my voice or my webcam?">
          Yes. The round red button next to Play opens the camera or the microphone, with a live
          picture and a level meter. Press <em>Record</em> and the timeline plays from the playhead
          while you talk or react; press <em>Stop</em> and the take lands on the timeline where you
          started, as a video track and an audio track. The recording is a file like any other: in
          Chrome and Edge you are asked where to save it, elsewhere it is downloaded, and it is never
          sent anywhere. Wear headphones if the timeline is playing, or the microphone will hear it.
        </Faq>
        <Faq q="Can I save and come back later?">
          Yes. Every edit is saved in this browser as you go, and the <em>Projects</em> button
          lists what is there. <em>Save project</em> downloads the same record as a small file you
          can keep or move to another computer. Either way the media stays yours to supply.
        </Faq>
        <Faq q="The picture is a grey box">
          That clip&rsquo;s file is not loaded: the lane shows <em>File missing</em> with a{" "}
          <em>Choose</em> button. Pick the file and the picture comes back. Exporting with a missing
          file gives you a grey box and silence for that clip, and the export dialog says so first.
        </Faq>
      </div>

      <p className="mt-6 text-[13px] leading-[1.6] text-muted">
        Step-by-step:{" "}
        <a className="link" href={`${HOME}/guides/trim-video-in-your-browser`}>
          trimming a video
        </a>
        ,{" "}
        <a className="link" href={`${HOME}/guides/reaction-video-picture-in-picture`}>
          putting a reaction cam in the corner
        </a>
        ,{" "}
        <a className="link" href={`${HOME}/guides/remove-background-noise-from-audio`}>
          removing background noise
        </a>
        ,{" "}
        <a className="link" href={`${HOME}/guides/add-text-to-a-video`}>
          adding titles and captions
        </a>
        ,{" "}
        <a className="link" href={`${HOME}/guides/vertical-video-for-reels-tiktok-shorts`}>
          making a Reel or a Short from landscape footage
        </a>
        . OtterStudio is part of <a className="link" href={HOME}>ottercraft.dev</a>, a few small free
        tools that run in your browser.
      </p>
    </section>
  );
}

function Step({ n, title, children }: { n: string; title: string; children: ReactNode }) {
  return (
    <li className="flex flex-col gap-1">
      <span className="font-mono text-[11px] text-teal-deep">{n}</span>
      <span className="font-serif text-[16px] font-semibold leading-tight text-pine">{title}</span>
      <p className="text-[13px] leading-[1.55] text-soft">{children}</p>
    </li>
  );
}

function Faq({ q, children }: { q: string; children: ReactNode }) {
  return (
    <details className="group py-3">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-serif text-[16px] leading-tight text-pine [&::-webkit-details-marker]:hidden">
        {q}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="shrink-0 text-muted transition-transform group-open:rotate-45" aria-hidden="true">
          <path d="M12 5v14" /><path d="M5 12h14" />
        </svg>
      </summary>
      <p className="mt-2 max-w-[720px] text-[14px] leading-[1.6] text-soft">{children}</p>
    </details>
  );
}

/** The three lines under the tool, shared with the prerendered shell. */
export function Footer() {
  return (
    <footer className="mt-16 flex flex-col gap-6 border-t border-rule pt-6">
      <ul className="flex min-w-0 flex-wrap items-end gap-x-8 gap-y-4">
        <Fact n="01" text="Files never leave your device" img="/otter/otter-lock.png" />
        <Fact n="02" text="Nothing uploaded, nothing stored" img="/otter/otter-stream.png" />
        <Fact n="03" text="Trim, layer, clean up, export" img="/otter/otter-p2p.png" />
      </ul>
      <nav aria-label="Site" className="flex flex-wrap items-center justify-between gap-x-5 gap-y-3 border-t border-rule/60 pt-4">
        <a
          href={HOME}
          className="flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-rule bg-surface pl-2 pr-3 text-[13px] font-medium text-soft transition-colors hover:border-pine hover:text-pine"
        >
          <img src="/logo.png" alt="" width="18" height="18" className="size-[18px]" />
          More tools
        </a>
        <SocialLinks className="mr-auto" />
        <p className="flex gap-4 whitespace-nowrap text-[12px] text-muted">
          <a href={`${HOME}/about`} className="link">About</a>
          <a href={`${HOME}/contact`} className="link">Contact</a>
          <a href="/privacy" className="link">Privacy</a>
          <a href="/terms" className="link">Terms</a>
          <AdConsentLink />
        </p>
      </nav>
    </footer>
  );
}

function Fact({ n, text, img }: { n: string; text: string; img: string }) {
  return (
    <li className="flex flex-col items-start gap-1.5">
      <img src={img} alt="" aria-hidden="true" className="h-9 w-auto" />
      <span className="flex items-baseline gap-2">
        <span className="font-mono text-[11px] text-pine">{n}</span>
        <span className="text-[13px] text-ink">{text}</span>
      </span>
    </li>
  );
}
